import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Brain, X, Check, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import {
  fetchProjectMemory,
  createProjectMemory,
  updateProjectMemory,
  deleteProjectMemory,
  MEMORY_TYPES,
  projectNotFoundMessage,
  isProjectNotFound,
  type Memory,
  type MemoryType,
} from "@/lib/api/projects";

interface Props {
  projectId: string;
  projectLabel: string;
}

const TYPE_LABELS: Record<MemoryType, string> = {
  fact: "Факт",
  decision: "Решение",
  preference: "Предпочтение",
  constraint: "Ограничение",
  architecture: "Архитектура",
  warning: "Предупреждение",
};

const TYPE_COLORS: Record<MemoryType, string> = {
  fact: "bg-sky-500/15 text-sky-400",
  decision: "bg-emerald-500/15 text-emerald-400",
  preference: "bg-amber-500/15 text-amber-400",
  constraint: "bg-rose-500/15 text-rose-400",
  architecture: "bg-violet-500/15 text-violet-400",
  warning: "bg-orange-500/15 text-orange-400",
};

const EMPTY_FORM = {
  type: "fact" as MemoryType,
  key: "",
  value: "",
  source: "",
  confidence: "",
};

export function ProjectMemory({ projectId, projectLabel }: Props) {
  const [items, setItems] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await fetchProjectMemory(projectId));
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось загрузить память проекта");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const startCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const startEdit = (m: Memory) => {
    setForm({
      type: m.type,
      key: m.key,
      value: m.value,
      source: m.source || "",
      confidence: m.confidence != null ? String(m.confidence) : "",
    });
    setEditingId(m.id);
    setShowForm(true);
  };

  const save = async () => {
    const key = form.key.trim();
    const value = form.value.trim();
    if (!key || !value || saving) return;
    const confidence = form.confidence.trim() === "" ? null : Number(form.confidence);
    if (confidence != null && (Number.isNaN(confidence) || confidence < 0 || confidence > 1)) return;
    const payload = {
      type: form.type,
      key,
      value,
      source: form.source.trim() || null,
      confidence,
    };
    setSaving(true);
    try {
      if (editingId) {
        await updateProjectMemory(projectId, editingId, payload);
      } else {
        await createProjectMemory(projectId, payload);
      }
      resetForm();
      await load();
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось сохранить запись");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setConfirmDelete(null);
    try {
      await deleteProjectMemory(projectId, id);
      await load();
    } catch (e) {
      setError(isProjectNotFound(e) ? projectNotFoundMessage() : "Не удалось удалить запись");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-primary">Память проекта</h2>
          <p className="text-xs text-tertiary mt-0.5">
            Факты, решения и ограничения, которые модель видит в контексте чатов проекта.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors"
          >
            <Plus size={15} />
            Добавить
          </button>
        )}
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <div className="rounded-xl border border-hairline bg-surface p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-secondary">
              {editingId ? "Редактирование записи" : "Новая запись"}
            </span>
            <button onClick={resetForm} className="text-tertiary hover:text-primary" aria-label="Закрыть">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-secondary mb-1.5" htmlFor="mem-type">Тип</label>
              <select
                id="mem-type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as MemoryType })}
                className="w-full h-9 px-2 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
              >
                {MEMORY_TYPES.map((t) => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-secondary mb-1.5" htmlFor="mem-key">Ключ</label>
              <input
                id="mem-key"
                value={form.key}
                maxLength={255}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                placeholder="например: db-engine"
                className="w-full h-9 px-3 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-secondary mb-1.5" htmlFor="mem-value">Значение</label>
            <textarea
              id="mem-value"
              value={form.value}
              rows={3}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              placeholder="Содержание записи (справочные данные, не инструкции)"
              className="w-full px-3 py-2 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent resize-y"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-secondary mb-1.5" htmlFor="mem-source">Источник (необязательно)</label>
              <input
                id="mem-source"
                value={form.source}
                maxLength={255}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="например: owner"
                className="w-full h-9 px-3 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-xs text-secondary mb-1.5" htmlFor="mem-conf">Уверенность 0–1 (необязательно)</label>
              <input
                id="mem-conf"
                value={form.confidence}
                onChange={(e) => setForm({ ...form, confidence: e.target.value })}
                placeholder="0.9"
                inputMode="decimal"
                className="w-full h-9 px-3 rounded-lg bg-hoverbg border border-hairline text-sm text-primary focus:outline-none focus:border-accent"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={() => void save()}
              disabled={saving || !form.key.trim() || !form.value.trim()}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {editingId ? "Сохранить" : "Добавить"}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-tertiary" />
        </div>
      ) : !error && items.length === 0 ? (
        <div className="py-12 flex flex-col items-center gap-3 text-center">
          <Brain size={28} className="text-tertiary" />
          <p className="text-sm text-secondary">Памяти пока нет.</p>
          <p className="text-xs text-tertiary max-w-xs">
            Добавьте факты, решения или ограничения — они попадут в контекст всех чатов проекта «{projectLabel}».
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li
              key={m.id}
              className="rounded-xl border border-hairline bg-surface px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn(
                      "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium",
                      TYPE_COLORS[m.type] ?? "bg-slate-500/15 text-slate-400",
                    )}>
                      {TYPE_LABELS[m.type] ?? m.type}
                    </span>
                    <span className="text-sm font-medium text-primary truncate">{m.key}</span>
                    {m.confidence != null && (
                      <span className="text-[11px] text-tertiary">увер. {m.confidence}</span>
                    )}
                    {m.source && (
                      <span className="text-[11px] text-tertiary">· {m.source}</span>
                    )}
                  </div>
                  <p className="mt-1.5 text-sm text-secondary whitespace-pre-wrap break-words">{m.value}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Tip label="Редактировать">
                    <button
                      onClick={() => startEdit(m)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
                      aria-label="Редактировать"
                    >
                      <Pencil size={14} />
                    </button>
                  </Tip>
                  <Tip label="Удалить">
                    <button
                      onClick={() => setConfirmDelete(m.id)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-secondary hover:text-rose-400 hover:bg-hoverbg transition-colors"
                      aria-label="Удалить"
                    >
                      <Trash2 size={14} />
                    </button>
                  </Tip>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div role="dialog" className="w-full max-w-sm rounded-2xl border border-hairline bg-surface p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Save size={18} className="text-rose-400" />
              <h3 className="text-sm font-medium text-primary">Удалить запись памяти?</h3>
            </div>
            <p className="text-sm text-secondary">
              Запись будет удалена из контекста проекта. Действие нельзя отменить.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="h-9 px-3.5 rounded-lg text-sm text-secondary hover:text-primary hover:bg-hoverbg transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => void remove(confirmDelete)}
                className="h-9 px-3.5 rounded-lg text-sm font-medium bg-rose-500/15 text-rose-400 hover:bg-rose-500/25 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
